import Q = require("q");

import TFS_Dashboards_Common = require("Dashboards/Scripts/Common");
import Dashboard_Shared_Contracts = require("Dashboards/Scripts/Contracts");
import TFS_Dashboards_Telemetry = require("Dashboards/Scripts/Telemetry");

import TFS_Host_TfsContext = require("Presentation/Scripts/TFS/TFS.Host.TfsContext");
import TFS_OM_Common = require("Presentation/Scripts/TFS/TFS.OM.Common");
import TFS_Dashboards_Constants = require("Dashboards/Scripts/Generated/Constants");
import Dashboards_WidgetContracts = require("TFS/Dashboards/WidgetContracts");
import WidgetHelpers = require("TFS/Dashboards/WidgetHelpers");

import Combos = require("VSS/Controls/Combos");
import VSS_Resources_Common = require("VSS/Resources/VSS.Resources.Common");
import TFS_Widget_Utilities = require("Widgets/Scripts/TFS.Widget.Utilities");
import WITOM = require("WorkItemTracking/Scripts/TFS.WorkItemTracking");
import { WorkItemManager } from "WorkItemTracking/Scripts/OM/WorkItemManager";
import { setClassificationNodesUsingMRU } from "WorkItemTracking/Scripts/Utils/WorkItemClassificationUtils";

/**
* Setting for the New Work Item widget
*/
export interface INewWorkItemConfiguration {
    defaultWorkItemType: string;
}

export class NewWorkItemWidget {

    /**
     * 
     * Static methods referenced by the NewWorkItemConfiguration code.
     * 
     */

    public static parseWorkItemTypeFromSettings(widgetSettings: Dashboards_WidgetContracts.WidgetSettings): string {
        var settings: INewWorkItemConfiguration = null;
        var workItemType: string = null;

        try {
            settings = JSON.parse(widgetSettings.customSettings.data);
        }
        catch (e) {
            // suppressing exception as we handle null configuration within load and Render. 
        }

        if (settings && settings.defaultWorkItemType) {
            workItemType = settings.defaultWorkItemType;
        }

        return workItemType;
    }

    public static getWorktItemTypeNames(projectWrapper: IProjectWrapper, projectId): IPromise<string[]> {
        var defer = Q.defer<string[]>();
        projectWrapper.getWorkItemTypeNames(projectId)
            .then((workItemTypes: string[]) => {
                if (workItemTypes.length == 0) {
                    // No Workitem Types have been configured for this project
                    defer.reject(VSS_Resources_Common.FeatureEnablementSettings_Error_Missing);
                }
                else {
                    defer.resolve(workItemTypes);
                }
            }, (e) => {
                var error: string = TFS_Widget_Utilities.ErrorParser.stringifyError(e);
                defer.reject(error);
            });
        return defer.promise;
    }

    /**
    * Set the default work item type
    * @workItemType : the string of the work item type
    */
    public static setDefaultWorkItemType(workItemType: string, workItemTypeList: string[], comboBox: Combos.Combo): void {
        if (workItemTypeList && comboBox) {
            var index = workItemTypeList.indexOf(workItemType);
            if (index > -1) {
                comboBox.setSelectedIndex(index, false);
            } else {
                comboBox.setSelectedIndex(0, false);
            }
        }
    }
}

/**
 * Options passed to ProjectWrapper constructor (used for dependency injection)
 */
export interface IProjectWrapperOptions {
    store: WITOM.WorkItemStore;
}

/**
 * Wraps work item management at project level
 */
export interface IProjectWrapper {
    createWorkItem(projectId: string, teamId: string, workItemTypeName: string): Q.IPromise<WITOM.WorkItem>;
    getWorkItemTypeNames(projectId: string): Q.IPromise<string[]>;
}

export class ProjectWrapper implements IProjectWrapper {
    private store: WITOM.WorkItemStore;
    private witManager: WorkItemManager;

    /**
     * Constructor
     * @param options Used for dependency injection in unit tests
     */
    constructor(options = <IProjectWrapperOptions>{}) {
        this.store = options.store || TFS_OM_Common.ProjectCollection.getDefaultConnection().getService<WITOM.WorkItemStore>(WITOM.WorkItemStore);

        this.witManager = WorkItemManager.get(this.store);
    }

    /**
     * Create a work item based on the type name
     * @param projectId The project id (a guid)
     * @param teamId The team id (a guid)
     * @param workItemTypeName The name of the work item type as returned by getWorkItemTypeNames
     */
    public createWorkItem(projectId: string, teamId: string, workItemTypeName: string): Q.IPromise<WITOM.WorkItem> {
        var deferred = Q.defer<WITOM.WorkItem>();

        this.store.beginGetProject(projectId, (project: WITOM.Project) => {
            project.beginGetWorkItemType(workItemTypeName, (workItemType: WITOM.WorkItemType) => {
                const workItem = WorkItemManager.get(this.store).createWorkItem(workItemType);
                setClassificationNodesUsingMRU(workItem, projectId).then(
                    () => {
                        workItem.resetManualFieldChanges();
                        deferred.resolve(workItem);
                    },
                    (error) => {
                        deferred.reject(error);
                    }
                );
            }, (error) => {
                deferred.reject(error);
            });
        }, (error) => {
            deferred.reject(error);
        });

        return deferred.promise;
    }

    /**
     * Gets a list of type names for work items that the user is able to create
     * @param projectId The project id (a guid)
     */
    public getWorkItemTypeNames(projectId: string): Q.IPromise<string[]> {
        var deferred = Q.defer<string[]>();

        this.store.beginGetProject(projectId, (project: WITOM.Project) => {
            project.beginGetVisibleWorkItemTypeNames((workItemTypes: string[]) => {
                deferred.resolve(workItemTypes);
            }, (error) => {
                deferred.reject(error);
            });
        }, (error) => {
            deferred.reject(error);
        });

        return deferred.promise;
    }
}